import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { Edit2, Trash2, Check, Pencil, Plus, AlertCircle } from "lucide-react";
import { SlipEntryModal } from "./SlipEntryModal";
import { useCurrency } from "@shared/selectors/globalSelectors";
import {
  useSlipLotEntries,
  useDeleteSlipLotEntry,
} from "@features/cx/paymentsv2/hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import { DeleteConfirmationDialog } from "@shared/components/DeleteConfirmationDialog";
import { Badge } from "@shared/ui/badge";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";

export const ManualTab = ({
  data,
  isLocked,
  disableAddEntry,
}: {
  data: any;
  isLocked?: boolean;
  disableAddEntry?: boolean;
}) => {
  const currency = useCurrency();
  const { id: lotId } = useParams<{ id: string }>();
  const [page, setPage] = useState(1);
  const limit = PAGE_SIZE;

  const deleteEntryMutation = useDeleteSlipLotEntry();

  const { data: entriesData, isLoading } = useSlipLotEntries({
    lotId,
    page,
    limit,
  });
  const entries = entriesData?.results || [];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<any>(null);

  const handleAddEntry = () => {
    setSelectedRow(null);
    setIsModalOpen(true);
  };

  const handleEditEntry = (row: any) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  const handleSubmit = (data: any, addAnother?: boolean) => {
    console.log("Submitting entry:", data);
    // Real implementation would update MANUAL_ENTRIES_DATA
  };

  const columns: TableColumn<any>[] = [
    {
      key: "consumerAccount",
      header: "Account No",
      render: (val: any, row: any) => (
        <div className="flex flex-col">
          <span className="font-semibold text-gray-900">
            {row.consumerAccount || "N/A"}
          </span>
          <span className="text-xs text-gray-500">
            {row.consumerName || "N/A"}
          </span>
        </div>
      ),
    },
    {
      key: "invoiceNumber",
      header: "Invoice No",
      render: (val: any, row: any) => (
        <span className="text-gray-500">{row.invoiceNumber || "N/A"}</span>
      ),
    },
    {
      key: "slipNumber",
      header: "Slip No",
      render: (val: any, row: any) => (
        <span className="text-gray-500">{row.slipNumber || "N/A"}</span>
      ),
    },
    {
      key: "paymentModeDisplay",
      header: "Mode",
      render: (val: any, row: any) => (
        <span className="text-gray-500">{row.paymentModeDisplay}</span>
      ),
    },
    {
      key: "amount",
      header: `Payment (${currency})`,
      render: (val: any, row: any) => (
        <span className="font-bold text-gray-900">{row.amount}</span>
      ),
    },
    {
      key: "centreCode",
      header: "Centre",
      render: (val: any, row: any) => (
        <span className="text-gray-500">{row.centreCode || "N/A"}</span>
      ),
    },
    {
      key: "paymentDate",
      header: "Date",
      render: (val: any, row: any) => (
        <span className="text-gray-500">{row.paymentDate || "N/A"}</span>
      ),
    },
    {
      key: "status_display",
      header: "Status",
      render: (val: any, row: any) => (
        <Badge
          className={getStatusBadgeClass(row.statusDisplay)}
        >
          {row.statusDisplay}
        </Badge>
      ),
    },
  ];

  const actions: TableAction<any>[] = [
    {
      label: "Edit",
      icon: <Pencil className="w-3.5 h-3.5" />,
      onClick: handleEditEntry,
      disabled: () => !!isLocked,
    },
    {
      label: "Delete",
      icon: <Trash2 className="w-3.5 h-3.5" />,
      onClick: (row: any) => {
        setRowToDelete(row);
        setDeleteModalOpen(true);
      },
      disabled: () => !!isLocked,
    },
  ];

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-3">
          <Pencil className="w-5 h-5 text-gray-700" />
          <h1 className="text-xl font-bold text-gray-900">Slip Entries</h1>
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={handleAddEntry}
          disabled={disableAddEntry}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Entry
        </Button>
      </div>
      <div className="p-0">
        <GenericTable
          data={entries}
          columns={columns}
          rowKey="id"
          actions={actions}
          actionsType="icons"
          onSearch={() => {}}
          pagination={{
            currentPage: page,
            totalPages: Math.ceil((entriesData?.count || 0) / limit) || 1,
            totalItems: entriesData?.count || 0,
            itemsPerPage: limit,
            startItem: (page - 1) * limit + 1,
            endItem: Math.min(page * limit, entriesData?.count || 0),
          }}
          onPageChange={(newPage) => setPage(newPage)}
        />
      </div>

      <SlipEntryModal
        data={data}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialData={selectedRow}
        onSubmit={handleSubmit}
      />

      <DeleteConfirmationDialog
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setRowToDelete(null);
        }}
        onConfirm={() => {
          if (rowToDelete) {
            deleteEntryMutation.mutate(rowToDelete.id, {
              onSuccess: () => {
                setDeleteModalOpen(false);
                setRowToDelete(null);
              },
            });
          }
        }}
        title="Delete Slip Entry"
        itemName={
          rowToDelete
            ? `Slip Entry (Acc: ${rowToDelete.consumerAccount || "Unknown"})`
            : undefined
        }
        isLoading={deleteEntryMutation.isPending}
      />
    </div>
  );
};
